<script setup lang="ts">
import type { ComponentPublicInstance } from "vue"
import { nextTick, ref } from "vue"
import type { DateStripItem } from "./index"

const props = defineProps<{
  items: DateStripItem[]
  modelValue: string
  ariaLabel: string
  formatShort: (value: string) => string
  formatLong: (value: string) => string
  currentLabel?: string
  todayLabel?: string
}>()

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

const cellRefs = ref<(HTMLElement | null)[]>([])

function select(item: DateStripItem) {
  if (item.disabled) return
  emit("update:modelValue", item.value)
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return
  e.preventDefault()

  const direction = e.key === "ArrowRight" ? 1 : -1
  const currentIndex = props.items.findIndex((i) => i.value === props.modelValue)
  let next = currentIndex + direction

  while (next >= 0 && next < props.items.length) {
    if (!props.items[next].disabled) {
      emit("update:modelValue", props.items[next].value)
      nextTick(() => {
        cellRefs.value[next]?.focus()
        cellRefs.value[next]?.scrollIntoView({ inline: "center", block: "nearest" })
      })
      return
    }
    next += direction
  }
}

function setCellRef(el: Element | ComponentPublicInstance | null, index: number) {
  cellRefs.value[index] = el as HTMLElement | null
}
</script>

<template>
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    class="date-strip"
    @keydown="handleKeydown"
  >
    <button
      v-for="(item, i) in items"
      :key="item.value"
      :ref="(el) => setCellRef(el, i)"
      type="button"
      role="radio"
      :aria-checked="item.value === modelValue"
      :aria-label="formatLong(item.value)"
      :tabindex="item.value === modelValue ? 0 : -1"
      :disabled="item.disabled"
      class="date-strip__cell"
      :class="{
        'date-strip__cell--selected': item.value === modelValue,
        'date-strip__cell--today': item.isToday,
        'date-strip__cell--current': item.isCurrent,
        'date-strip__cell--disabled': item.disabled,
      }"
      @click="select(item)"
    >
      <span class="date-strip__label">{{ formatShort(item.value) }}</span>
      <span
        v-if="item.isToday && todayLabel"
        class="date-strip__badge"
        aria-hidden="true"
      >{{ todayLabel }}</span>
      <span
        v-if="item.isCurrent && currentLabel"
        class="date-strip__badge date-strip__badge--current"
        aria-hidden="true"
      >{{ currentLabel }}</span>
    </button>
  </div>
</template>

<style scoped>
.date-strip {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: 4px;
  padding: 4px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.date-strip::-webkit-scrollbar {
  display: none;
}

.date-strip__cell {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 44px;
  min-height: 44px;
  padding: 6px 8px;
  flex-shrink: 0;
  scroll-snap-align: center;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--background);
  color: var(--foreground);
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.25;
  transition: background 0.15s, color 0.15s;
}

.date-strip__cell:hover:not(:disabled) {
  background: var(--accent);
  color: var(--accent-foreground);
}

.date-strip__cell:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

.date-strip__cell--selected {
  background: var(--primary);
  color: var(--primary-foreground);
  border-color: var(--primary);
}

.date-strip__cell--today::after {
  content: "";
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  margin-top: 2px;
}

.date-strip__cell--current {
  border-color: var(--primary);
  border-width: 2px;
}

.date-strip__cell--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.date-strip__label {
  font-weight: 500;
}

.date-strip__badge {
  font-size: 0.625rem;
  line-height: 1;
  color: var(--muted-foreground);
}

.date-strip__cell--selected .date-strip__badge {
  color: var(--primary-foreground);
  opacity: 0.8;
}
</style>
